/**
 * El catálogo de precios: what each product sells for on the lists chosen, by category, as a PDF to
 * send or print (the owner, 2026-10-19: «a function in the products page to make a catalog of
 * prices both wholesale and retail as a pdf that you can download»). Selling prices only: no
 * cost, no stock, nothing a customer should not be handed.
 *
 * Or without prices (the owner, 2026-11-04: «the option to download the catalog pdf WITHOUT any
 * prices also just the product and their different packagings»): every product on sale and its
 * presentations (`gruposSinPrecios`), drawn with no price columns.
 */
import { formatMoney, type Centavos } from '@abastece/shared';
import { precioId, ultimoDiaDeOferta, type ListaPrecio, type Oferta, type Producto, type UnidadVenta } from '@abastece/core';
import { unidadesDe } from './catalogo.js';
import { estadoDeOferta } from './ofertas.js';
import { CARTA, DocumentoPdf, anchoDeTexto, enRenglones, recortar, type Jpeg } from './pdf.js';

/** One presentation of one product, and its price on each list, in the lists' order. */
export interface FilaDelCatalogo {
  productoId: string;
  producto: string;
  presentacion: string;
  precios: (Centavos | null)[];
  /** The product's photo, by its name (`Producto.foto`), on each of its rows. */
  foto?: string | null;
  /** Under the name, smaller: an offer's price before it and its last day (2026-11-03). */
  nota?: string;
}

export interface GrupoDelCatalogo {
  categoria: string;
  filas: FilaDelCatalogo[];
}

/** The offers running on the day, for the catalogue's first group, and how it says them. */
export interface OfertasDelCatalogo {
  ofertas: readonly Oferta[];
  hoy: string;
  /** The group's name: «Ofertas». */
  titulo: string;
  /** Under a row's name: the list's price before it, when it has one, and its last day. */
  nota: (antes: Centavos | null, hasta: string) => string;
  /** Under the name of a row at so many for fewer: «2×1», what so many come to, and its last day. */
  notaLleva: (lleva: number, paga: number, porTodas: Centavos, hasta: string) => string;
}

/**
 * The products on sale with a price on at least one of `listas`, by category, each category and
 * each product in alphabetical order, a row for each of its presentations priced there, smallest
 * first: the packet, then the box of 12. A presentation priced on none of them is left out, and a
 * product with none left goes too.
 *
 * With `conOfertas`, the offers running that day on one of `listas` go first, a group of their own
 * (2026-11-03): each presentation an offer names, at its price on its list's column and nothing on
 * the others, the list's price before it and its last day under the name. One at so many for fewer
 * at the list's price, which it keeps, «2×1, lleva 2 por C$ 30.00» under the name. Their products
 * stay in their categories, at the list's own prices, which are theirs again once the offer ends.
 */
export function gruposDelCatalogo(
  productos: readonly Producto[],
  unidades: readonly UnidadVenta[],
  precios: readonly { id: string; precio: Centavos }[],
  listas: readonly Pick<ListaPrecio, 'id'>[],
  nombres: {
    producto: (p: Producto) => string;
    categoria: (categoria: string) => string;
    presentacion: (u: UnidadVenta, unidadBase: string) => string;
    locale: string;
  },
  conOfertas?: OfertasDelCatalogo,
): GrupoDelCatalogo[] {
  if (listas.length === 0) return [];
  const precioPor = new Map(precios.map((x) => [x.id, x.precio]));
  const grupos = enCategorias(productos, nombres, (p, producto) =>
    unidadesDe(unidades, p.id).flatMap((u) => {
      const suyos = listas.map((l) => precioPor.get(precioId(u.id, l.id)) ?? null);
      return suyos.some((x) => x !== null)
        ? [{ productoId: p.id, producto, presentacion: nombres.presentacion(u, p.unidadBase), precios: suyos, foto: p.foto ?? null }]
        : [];
    }),
  );
  const ofertas = conOfertas ? filasDeOfertas(productos, unidades, precioPor, listas, nombres, conOfertas) : [];
  return ofertas.length > 0 ? [{ categoria: conOfertas!.titulo, filas: ofertas }, ...grupos] : grupos;
}

/**
 * The catalogue without prices: every product on sale, by category, each category and each product
 * in alphabetical order, a row for each of its presentations, smallest first, priced or not. No
 * offers: they are prices.
 */
export function gruposSinPrecios(
  productos: readonly Producto[],
  unidades: readonly UnidadVenta[],
  nombres: {
    producto: (p: Producto) => string;
    categoria: (categoria: string) => string;
    presentacion: (u: UnidadVenta, unidadBase: string) => string;
    locale: string;
  },
): GrupoDelCatalogo[] {
  return enCategorias(productos, nombres, (p, producto) =>
    unidadesDe(unidades, p.id).map((u) => ({
      productoId: p.id,
      producto,
      presentacion: nombres.presentacion(u, p.unidadBase),
      precios: [],
      foto: p.foto ?? null,
    })),
  );
}

/**
 * The products on sale, each with the rows `filasDe` gives it, into their categories: the categories,
 * and the products in each, in alphabetical order. A product with no rows is left out.
 */
function enCategorias(
  productos: readonly Producto[],
  nombres: { producto: (p: Producto) => string; categoria: (categoria: string) => string; locale: string },
  filasDe: (p: Producto, producto: string) => FilaDelCatalogo[],
): GrupoDelCatalogo[] {
  const porCategoria = new Map<string, FilaDelCatalogo[][]>();
  for (const p of productos) {
    if (!p.activo) continue;
    const filas = filasDe(p, nombres.producto(p));
    if (filas.length === 0) continue;
    const categoria = nombres.categoria(p.categoria);
    const grupo = porCategoria.get(categoria);
    if (grupo) grupo.push(filas);
    else porCategoria.set(categoria, [filas]);
  }
  const enOrden = (a: string, b: string) => a.localeCompare(b, nombres.locale);
  return [...porCategoria]
    .sort(([a], [b]) => enOrden(a, b))
    .map(([categoria, deCadaUno]) => ({
      categoria,
      filas: deCadaUno.sort((a, b) => enOrden(a[0]!.producto, b[0]!.producto)).flat(),
    }));
}

/** The rows of the offers running on one of `listas`, by product name, each product's presentations smallest first. */
function filasDeOfertas(
  productos: readonly Producto[],
  unidades: readonly UnidadVenta[],
  precioPor: ReadonlyMap<string, Centavos>,
  listas: readonly Pick<ListaPrecio, 'id'>[],
  nombres: { producto: (p: Producto) => string; presentacion: (u: UnidadVenta, unidadBase: string) => string; locale: string },
  d: OfertasDelCatalogo,
): FilaDelCatalogo[] {
  const porId = new Map(productos.map((p) => [p.id, p]));
  const columna = new Map(listas.map((l, i) => [l.id, i]));
  const deCadaUno: FilaDelCatalogo[][] = [];
  for (const o of d.ofertas) {
    const p = porId.get(o.productoId);
    const i = columna.get(o.listaId);
    if (!p?.activo || i === undefined || estadoDeOferta(o, d.hoy) !== 'corre') continue;
    const suyas = new Map(o.precios.map((x) => [x.unidadVentaId, x]));
    const producto = nombres.producto(p);
    const hasta = ultimoDiaDeOferta(o);
    const filas = unidadesDe(unidades, p.id).flatMap((u) => {
      const suya = suyas.get(u.id);
      if (suya === undefined) return [];
      const antes = precioPor.get(precioId(u.id, o.listaId)) ?? null;
      // So many for fewer: at the list's price, the deal said under the name; none without a price.
      if ('lleva' in suya && antes === null) return [];
      const precio = 'lleva' in suya ? antes! : suya.precio;
      const nota = 'lleva' in suya ? d.notaLleva(suya.lleva, suya.paga, (antes! * suya.paga) as Centavos, hasta) : d.nota(antes !== null && antes > precio ? antes : null, hasta);
      return [
        {
          productoId: p.id,
          producto,
          presentacion: nombres.presentacion(u, p.unidadBase),
          precios: listas.map((_, j) => (j === i ? precio : null)),
          foto: p.foto ?? null,
          nota,
        },
      ];
    });
    if (filas.length > 0) deCadaUno.push(filas);
  }
  return deCadaUno.sort((a, b) => a[0]!.producto.localeCompare(b[0]!.producto, nombres.locale)).flat();
}

/** What the PDF says in the shop's language, and the shop as it introduces itself. */
export interface TextosDelCatalogo {
  negocio: string;
  /** Under the name: the address and the phone, where the shop has them. */
  contacto: string;
  titulo: string;
  fecha: string;
  /** The lists' names, in the order of each row's prices: over the prices on every card. */
  listas: readonly string[];
  /** A category carried over to the next page. */
  continua: (categoria: string) => string;
  pagina: (n: number, de: number) => string;
}

const MARGEN = 40;
const ARRIBA = 32;
/** Where what is listed must end, 12 points above the rule of the foot of the page. */
const ABAJO = 42;
/** A category's name and the rule under it, and the room after it before its first cards. */
const ALTO_CATEGORIA = 26;
/**
 * The cards, three across (the owner, 2026-11-04: «let's try this 3 card layout», when two-inch
 * photos one to a row made the shop's catalogue 79 pages), 12 points apart across and down: 169
 * points wide on US Letter. Three rows of them fit a page while their text is a line of name and two
 * presentations; a taller row leaves room for two.
 */
const COLUMNAS = 3;
const ENTRE = 12;
const ANCHO_TARJETA = (CARTA.ancho - 2 * MARGEN - (COLUMNAS - 1) * ENTRE) / COLUMNAS;
/**
 * A product's photo: a square two inches a side at the top of its card, in its middle, 8 points
 * under its top. Half an inch beside a row of a list until 2026-11-04, when the owner asked «can we
 * double the size of the product images in the generated pdfs?», then, of the inch that made,
 * «actually i wanted it double the size from where we doubled it».
 */
const LADO_FOTO = 144;
const MARGEN_FOTO = (ANCHO_TARJETA - LADO_FOTO) / 2;
const ARRIBA_FOTO = 7;
/** Between the photo and the name. */
const BAJO_FOTO = 5;
/** The text's margin inside a card, and its width. */
const RELLENO = 7;
const INTERIOR = ANCHO_TARJETA - 2 * RELLENO;
/** The name, in bold, and each of its lines. */
const LETRA_NOMBRE = 9;
const RENGLON_NOMBRE = 11;
/** A presentation and its prices, and each line of them. */
const LETRA = 8;
const RENGLON = 10;
/** The lists' names over the prices. */
const LETRA_LISTA = 6.5;
const ALTO_LISTAS = 8.5;
/** An offer's note, under its presentation. */
const LETRA_NOTA = 7;
const RENGLON_NOTA = 8.5;
/** Between a card's presentations, a hairline in the middle. */
const ENTRE_RENGLONES = 3;
/**
 * Each list's price, in a column at the card's right: «C$ 1,234.50» is 44 points at 8, 6 clear of
 * the next. A price wider than its column, C$ 10,000 and up, is written smaller to fit it.
 */
const COLUMNA_PRECIO = 50;
/**
 * The least a presentation keeps beside the prices' columns, 51 points with two lists: with less,
 * three lists and more, each list's price goes on a line of its own under the presentation, the
 * list's name before it.
 */
const MINIMO_PRESENTACION = 44;

/** A product's rows, together as `gruposDelCatalogo` lists them: they make one card. */
function porProducto(filas: readonly FilaDelCatalogo[]): FilaDelCatalogo[][] {
  const bloques: FilaDelCatalogo[][] = [];
  for (const f of filas) {
    const ultimo = bloques.at(-1);
    if (ultimo && ultimo[0]!.productoId === f.productoId) ultimo.push(f);
    else bloques.push([f]);
  }
  return bloques;
}

/** A product's card, its text cut into the lines it takes before anything is drawn. */
interface Tarjeta {
  /** Its photo, when this device has it to draw. */
  foto: { clave: string; jpeg: Jpeg } | null;
  nombre: string[];
  renglones: { presentacion: string[]; precios: (Centavos | null)[]; nota: string[] }[];
}

/**
 * The catalogue on US Letter, in cards three across (2026-11-04): the shop's name, what this is and
 * the day on the first page; then each category under its name and its products' cards in rows,
 * alphabetical across and down, a category that does not end on a page carried over under its name
 * again, and at the foot the shop, the day and «Página 2 de 5». A category's name never ends a page
 * without a row of its cards under it, and a row of cards is never split between two pages.
 *
 * A card: its product's photo two inches a side on top (`fotos`, the products' photos by name, as
 * JPEG), its name in bold under it, then each of its presentations, smallest first, a hairline
 * between them. With prices, each presentation's prices in a column for each list at the card's
 * right, the lists' names over them; past two lists, each list's price on a line of its own under
 * the presentation. An offer's note under its presentation. A name, a presentation or a note too
 * long for its line goes on in the lines under it rather than lose its end to «…».
 *
 * In a row with a photo every card keeps the photo's room, so that the names line up, and a card
 * whose product has none shows a pale square there. A row with no photo has no room for one: with
 * none to draw anywhere, the cards are their text alone.
 *
 * With no lists (`textos.listas` empty), the catalogue without prices: each card its name and its
 * presentations under it.
 */
export function catalogoEnPdf(
  grupos: readonly GrupoDelCatalogo[],
  textos: TextosDelCatalogo,
  fotos: ReadonlyMap<string, Jpeg> = new Map(),
): DocumentoPdf {
  const pdf = new DocumentoPdf();
  const { ancho, alto } = pdf;
  const derecha = ancho - MARGEN;
  const cuantasListas = textos.listas.length;
  /** The prices in columns beside the presentation, while it keeps its room: up to two lists. */
  const enColumnas = cuantasListas > 0 && INTERIOR - cuantasListas * COLUMNA_PRECIO >= MINIMO_PRESENTACION;
  /** What a presentation has beside the prices' columns, or the card's whole width. */
  const anchoPresentacion = enColumnas ? INTERIOR - cuantasListas * COLUMNA_PRECIO - 4 : INTERIOR;
  /** Where list `j`'s price ends on a card at `x`: its column's right, the last at the card's. */
  const finDePrecio = (x: number, j: number) => x + ANCHO_TARJETA - RELLENO - (cuantasListas - 1 - j) * COLUMNA_PRECIO;
  let y = 0;

  const tarjetaDe = (filas: readonly FilaDelCatalogo[]): Tarjeta => {
    const primera = filas[0]!;
    const jpeg = primera.foto ? fotos.get(primera.foto) : undefined;
    return {
      foto: jpeg ? { clave: primera.foto!, jpeg } : null,
      nombre: enRenglones(primera.producto, INTERIOR, 'negrita', LETRA_NOMBRE),
      renglones: filas.map((f) => ({
        presentacion: enRenglones(f.presentacion, anchoPresentacion, 'normal', LETRA),
        precios: f.precios,
        nota: f.nota ? enRenglones(f.nota, INTERIOR, 'normal', LETRA_NOTA) : [],
      })),
    };
  };

  /** A price as it is written, «—» where there is none, small enough for its column. */
  function precio(x: number, base: number, p: Centavos | null) {
    const s = p === null ? '—' : formatMoney(p);
    const tamano = Math.min(LETRA, (LETRA * (COLUMNA_PRECIO - 6)) / anchoDeTexto(s, 'normal', LETRA));
    pdf.texto(x, base, s, { tamano, gris: p === null ? 0.6 : 0, alinear: 'derecha' });
  }

  /**
   * A card's text, from `desde` down: drawn on the card at `x`, or, with `x` null, only measured.
   * Either way, how tall it is: the one walk through it, so what is measured is what is drawn.
   */
  function textoDeTarjeta(t: Tarjeta, x: number | null, desde: number): number {
    let arriba = desde;
    /** A line `suAlto` points tall, its text on a baseline four fifths down it. */
    const renglon = (suAlto: number, dibujar: (x: number, base: number) => void) => {
      if (x !== null) dibujar(x, arriba + suAlto * 0.8);
      arriba += suAlto;
    };
    for (const linea of t.nombre)
      renglon(RENGLON_NOMBRE, (x, base) => pdf.texto(x + RELLENO, base, linea, { fuente: 'negrita', tamano: LETRA_NOMBRE }));
    arriba += 2;
    if (enColumnas)
      renglon(ALTO_LISTAS, (x, base) =>
        textos.listas.forEach((lista, j) =>
          pdf.texto(finDePrecio(x, j), base, recortar(lista, COLUMNA_PRECIO - 4, 'negrita', LETRA_LISTA), {
            fuente: 'negrita',
            tamano: LETRA_LISTA,
            gris: 0.45,
            alinear: 'derecha',
          }),
        ),
      );
    t.renglones.forEach((r, i) => {
      if (i > 0) {
        if (x !== null) {
          const medio = arriba + ENTRE_RENGLONES / 2;
          pdf.linea(x + RELLENO, medio, x + ANCHO_TARJETA - RELLENO, medio, { grosor: 0.3, gris: 0.85 });
        }
        arriba += ENTRE_RENGLONES;
      }
      // Its first line has the prices beside it, when they go in columns.
      (r.presentacion.length > 0 ? r.presentacion : ['']).forEach((linea, k) =>
        renglon(RENGLON, (x, base) => {
          if (linea) pdf.texto(x + RELLENO, base, linea, { tamano: LETRA, gris: 0.35 });
          if (k === 0 && enColumnas) r.precios.forEach((p, j) => precio(finDePrecio(x, j), base, p));
        }),
      );
      // Past two lists, a line for each: its name, and its price at the card's right.
      if (!enColumnas)
        r.precios.forEach((p, j) =>
          renglon(RENGLON, (x, base) => {
            pdf.texto(x + RELLENO + 6, base, recortar(textos.listas[j]!, INTERIOR - 6 - COLUMNA_PRECIO - 4, 'normal', LETRA), {
              tamano: LETRA,
              gris: 0.45,
            });
            precio(x + ANCHO_TARJETA - RELLENO, base, p);
          }),
        );
      for (const linea of r.nota)
        renglon(RENGLON_NOTA, (x, base) => pdf.texto(x + RELLENO, base, linea, { tamano: LETRA_NOTA, gris: 0.4 }));
    });
    return arriba - desde;
  }

  /** A card's height: its photo's room when its row has one, its text, and its margins. */
  const altoDeTarjeta = (t: Tarjeta, conFoto: boolean) =>
    (conFoto ? ARRIBA_FOTO + LADO_FOTO + BAJO_FOTO : RELLENO) + textoDeTarjeta(t, null, 0) + RELLENO;
  /** A row's height: its tallest card's, which every card in it takes. */
  const altoDeFila = (fila: readonly Tarjeta[]) => {
    const conFoto = fila.some((t) => t.foto);
    return Math.max(...fila.map((t) => altoDeTarjeta(t, conFoto)));
  };

  /** A row of cards, from the left, each as tall as the row. */
  function filaDeTarjetas(fila: readonly Tarjeta[]) {
    const conFoto = fila.some((t) => t.foto);
    const suAlto = altoDeFila(fila);
    fila.forEach((t, c) => {
      const x = MARGEN + c * (ANCHO_TARJETA + ENTRE);
      pdf.marco(x, y, ANCHO_TARJETA, suAlto, { grosor: 0.6, gris: 0.8 });
      if (conFoto) {
        if (t.foto) pdf.imagen(x + MARGEN_FOTO, y + ARRIBA_FOTO, LADO_FOTO, LADO_FOTO, t.foto.clave, t.foto.jpeg);
        else pdf.banda(x + MARGEN_FOTO, y + ARRIBA_FOTO, LADO_FOTO, LADO_FOTO, 0.95);
      }
      textoDeTarjeta(t, x, y + (conFoto ? ARRIBA_FOTO + LADO_FOTO + BAJO_FOTO : RELLENO));
    });
    y += suAlto + ENTRE;
  }

  function nuevaPagina() {
    pdf.nuevaPagina();
    y = ARRIBA;
  }

  function categoria(nombre: string) {
    pdf.texto(MARGEN, y + 15, recortar(nombre, derecha - MARGEN, 'negrita', 10), { fuente: 'negrita', tamano: 10 });
    pdf.linea(MARGEN, y + 20, derecha, y + 20, { grosor: 0.8, gris: 0.35 });
    y += ALTO_CATEGORIA;
  }

  // The first page: who, what and when, over the cards.
  pdf.nuevaPagina();
  y = ARRIBA;
  pdf.texto(MARGEN, y + 18, recortar(textos.negocio, derecha - MARGEN, 'negrita', 18), { fuente: 'negrita', tamano: 18 });
  y += 36;
  pdf.texto(MARGEN, y, `${textos.titulo} · ${textos.fecha}`, { tamano: 10, gris: 0.3 });
  if (textos.contacto) {
    y += 14;
    pdf.texto(MARGEN, y, recortar(textos.contacto, derecha - MARGEN, 'normal', 9), { tamano: 9, gris: 0.45 });
  }
  y += 10;

  const cabe = (cuanto: number) => y + cuanto <= alto - ABAJO;
  for (const g of grupos) {
    const tarjetas = porProducto(g.filas).map(tarjetaDe);
    const filas = Array.from({ length: Math.ceil(tarjetas.length / COLUMNAS) }, (_, i) => tarjetas.slice(i * COLUMNAS, (i + 1) * COLUMNAS));
    if (filas.length === 0) continue;
    if (!cabe(ALTO_CATEGORIA + altoDeFila(filas[0]!))) nuevaPagina();
    categoria(g.categoria);
    filas.forEach((fila, i) => {
      if (i > 0 && !cabe(altoDeFila(fila))) {
        nuevaPagina();
        categoria(textos.continua(g.categoria));
      }
      filaDeTarjetas(fila);
    });
  }

  // The foot of each page, now that it is known how many there are.
  const pie = `${textos.negocio} · ${textos.titulo} · ${textos.fecha}`;
  for (let i = 0; i < pdf.cuantasPaginas; i++) {
    pdf.irAPagina(i);
    pdf.linea(MARGEN, alto - 34, derecha, alto - 34, { grosor: 0.4, gris: 0.8 });
    pdf.texto(MARGEN, alto - 22, recortar(pie, derecha - MARGEN - 90, 'normal', 8), { tamano: 8, gris: 0.45 });
    pdf.texto(derecha, alto - 22, textos.pagina(i + 1, pdf.cuantasPaginas), { tamano: 8, gris: 0.45, alinear: 'derecha' });
  }
  return pdf;
}
